import { immerable } from '@utils';
import type { Elements } from './Elements';
import { Forest, type BlankForest } from './Forest';
import { Graph } from './Graph';
import type { MutableCompound } from './interface/MutableCompound';
import type { EdgeDirection, Edges, EID, NID, Nodes } from './types';

//
// A compound graph is a graph with a parallel forest that puts the nodes of the
// graph into a hierarchy.
//
// The sets of edges of the graph and the forest are disjoint. The set of nodes
// between the graph and the tree are identical.
//

export type BlankCompound = Compound<unknown, unknown, unknown>;

export class Compound<G, N, E> implements MutableCompound<G, N, E> {
  [immerable] = true;

  private _graph: Graph<G, N, E>;
  private _forest: BlankForest;

  constructor(opts?: { graph: Graph<G, N, E>; forest: BlankForest } | { data?: G }) {
    if (opts !== undefined && 'graph' in opts) {
      const { graph, forest } = opts;
      if (
        graph.nodeCount !== forest.nodeCount ||
        graph.nodes.symmetricDifference(forest.nodes).size !== 0
      ) {
        throw new Error('Graph and forest nodes must be identical');
      }
      this._graph = graph;
      this._forest = forest;
    } else {
      this._graph = new Graph<G, N, E>(opts);
      this._forest = new Forest<G, N, E>();
    }
  }

  // Get the graph of the compound.
  //
  // Note: This is for read-only traversal and analysis purposes. Do not mutate
  // the returned object.
  get graph(): Graph<G, N, E> {
    return this._graph;
  }

  // Get the forest of the compound.
  //
  // Note: This is for read-only traversal and analysis purposes. Do not mutate
  // the returned object.
  get forest(): BlankForest {
    return this._forest;
  }

  //
  // VisitableGraph
  //

  get isEmpty(): boolean {
    return this._graph.isEmpty;
  }

  get nodeCount(): number {
    return this._graph.nodeCount;
  }

  get edgeCount(): number {
    return this._graph.edgeCount;
  }

  get nodes(): Nodes {
    return this._graph.nodes;
  }

  get edges(): Edges {
    return this._graph.edges;
  }

  get elements(): Elements {
    return this._graph.elements;
  }

  hasNode(id: NID): boolean {
    return this._graph.hasNode(id);
  }

  hasEdge(id: EID): boolean {
    return this._graph.hasEdge(id);
  }

  hasEdgeFromTo(source: NID, target: NID): boolean {
    return this._graph.hasEdgeFromTo(source, target);
  }

  hasEdgeBetween(a: NID, b: NID): boolean {
    return this._graph.hasEdgeBetween(a, b);
  }

  source(id: EID): NID {
    return this._graph.source(id);
  }

  target(id: EID): NID {
    return this._graph.target(id);
  }

  endpoints(id: EID): [NID, NID] {
    return this._graph.endpoints(id);
  }

  endpoint(id: EID, direction: EdgeDirection): NID {
    return this._graph.endpoint(id, direction);
  }

  outEdges(id: NID): Edges {
    return this._graph.outEdges(id);
  }

  inEdges(id: NID): Edges {
    return this._graph.inEdges(id);
  }

  incidentEdges(id: NID): Edges {
    return this._graph.incidentEdges(id);
  }

  outDegree(id: NID): number {
    return this._graph.outDegree(id);
  }

  inDegree(id: NID): number {
    return this._graph.inDegree(id);
  }

  degree(id: NID): number {
    return this._graph.degree(id);
  }

  successors(id: NID | Nodes): Nodes {
    return this._graph.successors(id);
  }

  transitiveSuccessors(id: NID | Nodes): Nodes {
    return this._graph.transitiveSuccessors(id);
  }

  predecessors(id: NID | Nodes): Nodes {
    return this._graph.predecessors(id);
  }

  transitivePredecessors(id: NID | Nodes): Nodes {
    return this._graph.transitivePredecessors(id);
  }

  neighbors(id: NID): Nodes {
    return this._graph.neighbors(id);
  }

  hasSuccessors(id: NID): boolean {
    return this._graph.hasSuccessors(id);
  }

  hasPredecessors(id: NID): boolean {
    return this._graph.hasPredecessors(id);
  }

  hasNeighbors(id: NID): boolean {
    return this._graph.hasNeighbors(id);
  }

  get roots(): Nodes {
    return this._graph.roots;
  }

  get leaves(): Nodes {
    return this._graph.leaves;
  }

  get nonRoots(): Nodes {
    return this._graph.nonRoots;
  }

  get nonLeaves(): Nodes {
    return this._graph.nonLeaves;
  }

  get internals(): Nodes {
    return this._graph.internals;
  }

  isRoot(id: NID): boolean {
    return this._graph.isRoot(id);
  }

  isLeaf(id: NID): boolean {
    return this._graph.isLeaf(id);
  }

  isInternal(id: NID): boolean {
    return this._graph.isInternal(id);
  }

  //
  // VisitableData
  //

  get data(): G {
    return this._graph.data;
  }

  nodeData(id: NID): N {
    return this._graph.nodeData(id);
  }

  edgeData(id: EID): E {
    return this._graph.edgeData(id);
  }

  //
  // VisitableTreeish
  //

  inEdge(node: NID): EID | undefined {
    return this._forest.inEdge(node);
  }

  parent(node: NID): NID | undefined {
    return this._forest.parent(node);
  }

  hasParent(node: NID): boolean {
    return this._forest.hasParent(node);
  }

  children(node: NID): Nodes {
    return this._forest.children(node);
  }

  hasChildren(node: NID): boolean {
    return this._forest.hasChildren(node);
  }

  childCount(node: NID): number {
    return this._forest.childCount(node);
  }

  descendants(node: NID): Nodes {
    return this._forest.descendants(node);
  }

  hasDescendant(node: NID, descendant: NID): boolean {
    return this._forest.hasDescendant(node, descendant);
  }

  ancestors(node: NID): Nodes {
    return this._forest.ancestors(node);
  }

  hasAncestor(node: NID, ancestor: NID): boolean {
    return this._forest.hasAncestor(node, ancestor);
  }

  pathToRoot(node: NID): NID[] {
    return this._forest.pathToRoot(node);
  }

  depth(node: NID): number {
    return this._forest.depth(node);
  }

  //
  // VisitableForest
  //

  leastCommonAncestor(nodes: Nodes): NID | undefined {
    return this._forest.leastCommonAncestor(nodes);
  }

  //
  // MutableGraphBase
  //

  addEdge(id: EID, source: NID, target: NID, data?: E): this {
    this._graph.addEdge(id, source, target, data);
    return this;
  }

  removeEdge(id: EID): this {
    this._graph.removeEdge(id);
    return this;
  }

  clearEdges(id: NID): this {
    this._graph.clearEdges(id);
    return this;
  }

  moveEdge(
    id: EID,
    ends: { newSource?: NID | undefined; newTarget?: NID | undefined }
  ): this {
    this._graph.moveEdge(id, ends);
    return this;
  }

  //
  // MutableData
  //

  setData(data: G): this {
    this._graph.setData(data);
    return this;
  }

  setNodeData(id: NID, data: N): this {
    this._graph.setNodeData(id, data);
    return this;
  }

  setEdgeData(id: EID, data: E): this {
    this._graph.setEdgeData(id, data);
    return this;
  }

  withData(f: (data: G) => void): this {
    this._graph.withData(f);
    return this;
  }

  withNodeData(id: NID, f: (data: N) => void): this {
    this._graph.withNodeData(id, f);
    return this;
  }

  withEdgeData(id: EID, f: (data: E) => void): this {
    this._graph.withEdgeData(id, f);
    return this;
  }

  //
  // MutableForest
  //

  addNode(
    id: NID,
    opts?:
      | { data?: N } // Add as root
      | { data?: N; parent: NID; newEdge: EID } // Add as child
  ): this {
    this._graph.addNode(id, opts);
    const forestOptions = { ...opts } as { data?: N };
    delete forestOptions.data;
    this._forest.addNode(id, forestOptions);
    return this;
  }

  moveNode(
    id: NID,
    opts?: { newParent: NID } | { newParent: NID; newEdge: EID; edgeData?: E }
  ): this {
    this._forest.moveNode(id, opts);
    return this;
  }

  //
  // MutableTreeBase
  //

  removeNodeUngrouping(id: NID): this {
    this._graph.removeNode(id);
    this._forest.removeNodeUngrouping(id);
    return this;
  }

  removeNodeAndDescendants(id: NID): Nodes {
    const removed = this._forest.removeNodeAndDescendants(id);
    removed.forEach(node => this._graph.removeNode(node));
    return removed;
  }

  removeDescendants(id: NID): Nodes {
    const removed = this._forest.removeDescendants(id);
    removed.forEach(node => this._graph.removeNode(node));
    return removed;
  }
}
